import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { api } from '@/api/client';
import type { AuctionDetail, AuctionFilters } from '@/types/domain';

export type AuctionListFilters = Omit<AuctionFilters, 'page'>;

export const queryKeys = {
  auctions: (filters: AuctionListFilters) => ['auctions', filters] as const,
  auction: (id: string) => ['auction', id] as const,
  bids: (auctionId: string) => ['bids', auctionId] as const,
  tags: () => ['tags'] as const,
};

const DEFAULT_PER_PAGE = 20;

export function useInfiniteAuctions(filters: AuctionListFilters = {}) {
  const perPage = filters.per_page ?? DEFAULT_PER_PAGE;

  return useInfiniteQuery({
    queryKey: queryKeys.auctions(filters),
    queryFn: ({ pageParam }) =>
      api.listAuctions({ ...filters, page: pageParam, per_page: perPage }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.page + 1 : undefined,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useAuction(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.auction(id ?? ''),
    queryFn: () => api.getAuction(id!),
    enabled: !!id,
    staleTime: 0,
    refetchInterval: 10_000,
  });
}

export function useBids(auctionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bids(auctionId ?? ''),
    queryFn: () => api.listBids(auctionId!),
    enabled: !!auctionId,
    staleTime: 5_000,
    refetchInterval: 45_000,   // ← было 10_000; WS инвалидирует при ставке
  });
}

export function useTags() {
  return useQuery({
    queryKey: queryKeys.tags(),
    queryFn: () => api.listTags(),
    staleTime: 5 * 60_000,
  });
}

export function usePlaceBid(auctionId: string) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (amount: number) => api.placeBid(auctionId, amount),
      onSuccess: ({ auction: updated }) => {
        qc.setQueryData<AuctionDetail>(queryKeys.auction(auctionId), (prev) => ({
          ...updated,
          my_auto_bid: prev?.my_auto_bid ?? updated.my_auto_bid,
          my_review: prev?.my_review ?? updated.my_review,
        }));
  
        qc.invalidateQueries({
          queryKey: queryKeys.bids(auctionId),
          refetchType: 'active',
        });
        qc.invalidateQueries({
          queryKey: ['auctions'],
          refetchType: 'active',
        });
        qc.invalidateQueries({ queryKey: ['my-auctions'] });
  
      },
      onError: (err) => {
        console.error('[usePlaceBid] ОШИБКА:', err);
      },
    });
  }

  export function useBuyNow(auctionId: string) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: () => api.buyNow(auctionId),
      onSuccess: (updated) => {
        qc.setQueryData(queryKeys.auction(auctionId), updated);
        qc.invalidateQueries({ queryKey: queryKeys.bids(auctionId) });
        qc.invalidateQueries({ queryKey: ['my-auctions'] });
      },
    });
  }

export function useSetAutoBid(auctionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ maxAmount, stepAmount }: { maxAmount: number; stepAmount: number | null }) =>
      api.setAutoBid(auctionId, maxAmount, stepAmount),
    onSuccess: ({ auto_bid, auction }) => {
      qc.setQueryData<AuctionDetail>(queryKeys.auction(auctionId), (prev) => ({
        ...auction,
        my_auto_bid: auto_bid,
        my_review: prev?.my_review ?? null,
      }));

      qc.invalidateQueries({
        queryKey: queryKeys.bids(auctionId),
        refetchType: 'active',
      });
      qc.invalidateQueries({
        queryKey: ['auctions'],
        refetchType: 'active',
      });
    },
  });
}

export function useCreateAuction() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (input: Parameters<typeof api.createAuction>[0]) => api.createAuction(input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ['auctions'] });
      },
    });
  }

  export function useUpdateAuction(auctionId: string) {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (input: Parameters<typeof api.updateAuction>[1]) =>
        api.updateAuction(auctionId, input),
      onSuccess: (updated) => {
        qc.setQueryData(queryKeys.auction(auctionId), updated);
      },
    });
  }